import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Navbar from "./components/navbar/Navbar";
import { ConfigProvider } from "antd";
import {
  ClerkLoaded,
  useOrganizationList,
  ClerkLoading,
  SignedIn,
  SignedOut,
  useClerk,
  useUser,
} from "@clerk/clerk-react";
import gtm from "@/utils/gtm";
import Tracker from "@/utils/track";
import "./App.css";
import GlobalAlert from "@/components/GlobalAlert";
import LanguageInitializer from "@/components/LanguageInitializer";
// 导入i18n配置
import "./i18n";

// 导入样式
import { isMobile } from "./biz/tool";
import TimModal from "@/tim";
import { useTimBackgroundInit } from "@/tim/useTimBackgroundInit";
import { parseTimOpenSource, useTimModalStore } from "@/tim/useTimModalStore";
import EmailUnsubscribe from "@/pages/EmailUnsubscribe";
import Intercom from "@intercom/messenger-js-sdk";
import FeatureUpdateReminder from "./components/FeatureUpdateReminder";
import { Loading } from "aha-components";

const Home = React.lazy(
  () => import(/* webpackChunkName: "pages~home" */ "./pages/home"),
);
const Campaign = React.lazy(
  () => import(/* webpackChunkName: "pages~campaign" */ "./pages/campaign"),
);
const ChatBot = React.lazy(
  () =>
    import(/* webpackChunkName: "pages~headChatBot" */ "./pages/headChatBot"),
);
const ShareChatBot = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~headChatBot~share" */ "./pages/headChatBot/share"
    ),
);
const ChatBotOnboarding = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~headChatBot~indexChatBotOnboarding" */ "./pages/headChatBot/indexChatBotOnboarding"
    ),
);
const CampaignDetail = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail" */ "./pages/campaignDetail"
    ),
);
const PaymentResult = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~paymentResult" */ "./pages/payment/paymentResult"
    ),
);
const CampaignCreate = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignCreate" */ "./pages/campaignCreate"
    ),
);
const Plan = React.lazy(
  () => import(/* webpackChunkName: "pages~Plan" */ "./pages/Plan"),
);
const Coupon = React.lazy(
  () => import(/* webpackChunkName: "pages~Coupon" */ "./pages/Coupon"),
);
const Collaboration = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail~pages~collaboration" */ "@/pages/campaignDetail/pages/collaboration"
    ),
);
const SalesOutreach = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail~pages~salesOutreach" */ "@/pages/campaignDetail/pages/salesOutreach"
    ),
);
const CreateOrganization = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~createOrganization" */ "./pages/createOrganization"
    ),
);
const OrganizationProfile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~organizationProfile" */ "./pages/organizationProfile"
    ),
);
const Onboarding = React.lazy(
  () => import(/* webpackChunkName: "pages~onboarding" */ "./pages/onboarding"),
);
const ReportsComponent = React.lazy(
  () => import(/* webpackChunkName: "pages~Reports" */ "./pages/Reports"),
);
const OrderDetail = React.lazy(
  () =>
    import(/* webpackChunkName: "pages~orderDetail" */ "@/pages/orderDetail"),
);
const Reports = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail~pages~reports" */ "@/pages/campaignDetail/pages/reports"
    ),
);
const Budget = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail~pages~budget" */ "@/pages/campaignDetail/pages/budget"
    ),
);

const CampaignInfo = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~campaignDetail~pages~campaignInfo" */ "@/pages/campaignDetail/pages/campaignInfo"
    ),
);
const IntegrationGuide = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~integrationGuide" */ "./pages/integrationGuide"
    ),
);
const Icons = React.lazy(
  () => import(/* webpackChunkName: "pages~icons" */ "./pages/icons"),
);
const Login = React.lazy(
  () => import(/* webpackChunkName: "pages~login" */ "./pages/login/login"),
);
const Signup = React.lazy(
  () => import(/* webpackChunkName: "pages~signup" */ "./pages/login/signup"),
);
const MLogin = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~login~SignAndSignUpMobile" */ "./pages/login/SignAndSignUpMobile"
    ),
);
const ViralIdeas = React.lazy(
  () => import(/* webpackChunkName: "pages~ViralIdeas" */ "./pages/ViralIdeas"),
);
const HeadsDna = React.lazy(
  () => import(/* webpackChunkName: "pages~HeadsDna" */ "./pages/HeadsDna"),
);
const AfterRelease = React.lazy(
  () =>
    import(/* webpackChunkName: "pages~AfterRelease" */ "./pages/AfterRelease"),
);
const BookDemoPage = React.lazy(
  () => import(/* webpackChunkName: "pages~BookDemoPage" */ "./pages/BookDemo"),
);
const TransitPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "pages~TransitPage" */ "./pages/login/TransitPage"
    ),
);

const ThankYou = React.lazy(
  () => import(/* webpackChunkName: "pages~ThankYou" */ "./pages/ThankYou"),
);

const HIDE_NAV_PATHNAMES = [
  "/login",
  "/signup",
  "/campaign/create",
  "/campaign/website/create",
  "/create-organization",
  "/onboarding",
  "/integration-guide",
  "/mLogin",
  "/headOnboarding",
  "/afterRelease",
  "/transit",
  "/email-unsubscribe",
  "/book-demo",
];

const HIDE_CLERK_LOADING_PATHNAMES = ["/login", "/signup"];

function App() {
  // console.log("渲染就打印 - App.tsx");
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { isSignedIn, user, isLoaded } = useUser();
  const { setShowTimModal } = useTimModalStore();
  useTimBackgroundInit();
  const isShowNav = !HIDE_NAV_PATHNAMES.some((path) => pathname.includes(path));

  // Initialize Intercom on sign-in, shutdown on sign-out
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) {
      const createdAtInMs = user?.createdAt?.valueOf() || new Date().valueOf();
      const createdAt = Math.round(createdAtInMs / 1000);
      Intercom({
        app_id: "n31s0evy",
        user_id: user?.id,
        name: user?.fullName || "",
        email: user?.primaryEmailAddress?.emailAddress,
        created_at: createdAt,
      });
    } else {
      try {
        (window as any).Intercom?.("shutdown");
      } catch (_) {}
    }
  }, [
    isLoaded,
    isSignedIn,
    user?.id,
    user?.fullName,
    user?.primaryEmailAddress,
    user?.createdAt,
  ]);

  // Notify Intercom on SPA route changes
  useEffect(() => {
    try {
      (window as any).Intercom?.("update");
    } catch (_) {}
  }, [pathname]);

  useEffect(() => {
    // console.log("渲染就打印 - App.tsx useeffect");
    gtm.init();
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    const query = new URLSearchParams(location.search);
    if (query.get("tim") !== "1") return;

    const source = parseTimOpenSource(query.get("source"));
    setShowTimModal(true, { source });
    query.delete("tim");
    query.delete("source");

    navigate(
      {
        pathname: location.pathname,
        search: query.toString() ? `?${query.toString()}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [
    isLoaded,
    isSignedIn,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    setShowTimModal,
  ]);

  // const ModalBox = () => {
  //   return (
  //     <>
  //       <ModalAddPayment />
  //       <ModalPaymentBoundCard />
  //       <ModalUpdatePlan />
  //     </>
  //   );
  // }

  return (
    <div className={`aha-app`}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#FB6011",
            // colorInfo: "#c4330a",
            fontFamily: '"DM Sans"',
          },
          components: {
            Table: {
              headerBg: "#fff",
              headerSplitColor: "#fff",
              headerColor: "#71717A",
              footerBg: "#fff",
            },
            Radio: {
              buttonBg: "#FB6011",
              buttonCheckedBg: "#FB6011",
              buttonSolidCheckedActiveBg: "#FB6011",
              buttonSolidCheckedBg: "#FB6011",
            },
            Button: {
              // defaultActiveBg: "#c4330a",
              // defaultHoverBorderColor: "#c4330a",
            },
            Message: {
              zIndexPopupBase: 1000100,
              zIndexPopup: 1000100,
            },
            InputNumber: {
              addonBg: "#fff",
            },
            Input: {
              addonBg: "#fff",
              activeBorderColor: "#FB6011",
              hoverBorderColor: "#FB6011",
            },
            Progress: {
              defaultColor: "#FB6011",
            },
            Tabs: {
              itemActiveColor: "#FB6011",
              itemHoverColor: "#FB6011",
              itemSelectedColor: "#FB6011",
              inkBarColor: "#FB6011",
            },
          },
        }}
      >
        <Navbar isShowNav={isShowNav} />
        <div className={`${isMobile() ? "w-full" : "aha-content"}`}>
          <SignedIn>
            <ClerkLoaded>
              <LanguageInitializer />
              <RedirectForClerk />
            </ClerkLoaded>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/campaign" element={<Campaign />} />
              <Route path="/report" element={<ReportsComponent />} />
              <Route path="/head" element={<HeadsDna />} />
              {/* <Route path="/head" element={<ChatBot />} /> */}
              <Route path="/share" element={<ShareChatBot />} />
              <Route path="/headOnboarding" element={<ChatBotOnboarding />} />
              {process.env.REACT_APP_ENV !== "production" && (
                <Route path="/icons" element={<Icons />} />
              )}
              <Route path="/campaign/detail/:id" element={<CampaignDetail />}>
                <Route
                  path="/campaign/detail/:id/collaboration"
                  element={<Collaboration />}
                />
                <Route
                  path="/campaign/detail/:id/salesOutreach"
                  element={<SalesOutreach />}
                />
                <Route
                  path="/campaign/detail/:id/reports"
                  element={<Reports />}
                />
                <Route
                  path="/campaign/detail/:id/budget"
                  element={<Budget />}
                />
                <Route
                  path="/campaign/detail/:id/info"
                  element={<CampaignInfo />}
                />
                <Route
                  path="/campaign/detail/:id/collaboration/:orderId"
                  element={<OrderDetail />}
                />
              </Route>
              <Route
                path="/campaign/create/:stage"
                element={<CampaignCreate />}
              />
              <Route
                path="/payment"
                element={<Navigate to="/organization#/payment" />}
              />
              <Route
                path="/plan"
                // element={<Navigate to="/organization#/plan" />}
                element={<Plan />}
              />
              <Route path="/coupon" element={<Coupon />} />
              <Route path="/payment/result" element={<PaymentResult />} />
              <Route
                path="/create-organization"
                element={<CreateOrganization />}
              />
              <Route path="/organization" element={<OrganizationProfile />} />
              <Route path="/onboarding" element={<Navigate to="/" />} />
              <Route path="/viralIdeas" element={<ViralIdeas />} />
              <Route path="/headsDna" element={<HeadsDna />} />
              <Route path="/afterRelease" element={<AfterRelease />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/transit" element={<TransitPage />} />
              {/* <Route path="/thank-you" element={<ThankYou />} /> */}
              {/* <Route path="/bindPayment" element={<BindPayment dpmCheckerLink={dpmCheckerLink} />} /> */}
            </Routes>
            <TimModal />
            {/* 暂停的campaign列表全局通知 */}
            <GlobalAlert />
            {/* 功能更新提醒弹窗 */}
            <FeatureUpdateReminder />
          </SignedIn>
          <SignedOut>
            <Routes>
              <Route path="/share" element={<ShareChatBot />} />
            </Routes>
          </SignedOut>
        </div>
        <ClerkLoaded>
          <Routes>
            <Route
              path="/integration-guide/:type"
              element={<IntegrationGuide />}
            />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/mLogin" element={<MLogin />} />
            <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
            <Route
              path="/campaign/website/create/:stage"
              element={<CampaignCreate />}
            />
          </Routes>
        </ClerkLoaded>
      </ConfigProvider>
      {!HIDE_CLERK_LOADING_PATHNAMES.some((path) =>
        pathname.includes(path),
      ) && (
        <ClerkLoading>
          <div className="flex flex-col justify-center items-center h-[calc(60vh)] w-full">
            <Loading width={50} height={50} color="#000" strokeWidth={30} />
            <div className="text-gray-500 text-sm">Loading resources...</div>
          </div>
        </ClerkLoading>
      )}
      {/* 右下角的联系方式等提示组件 */}
      {/* <ContactUs /> */}
    </div>
  );
}

export default App;

export const RedirectForClerk = () => {
  const { user } = useUser();

  const navigate = useNavigate();
  const clerk = useClerk();
  const {
    setActive,
    userMemberships: { isLoading, count, data },
  } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });

  useEffect(() => {
    if (user?.id) {
      // 获取utm_medium和utm_source
      const utmMedium = window.localStorage.getItem("utm_medium");
      const utmSource = window.localStorage.getItem("utm_source");
      // console.log('utmMedium', utmMedium);
      // console.log('utmSource', utmSource);
      Tracker.gtag("event", "click", {
        event_category: "platform_ads_sign_success",
        utm_source: utmSource || "none",
        utm_medium: utmMedium || "none",
        value: 1,
      });
    }

    if (setActive && !isLoading && clerk.loaded) {
      if (count === 1) {
        setActive({ organization: data?.[0]?.organization?.id });
      } else if (count === 0) {
        // const utmMedium = window.localStorage.getItem("utm_medium");
        // const utmSource = window.localStorage.getItem("utm_source");
        // const firstUtmSource = window.localStorage.getItem("first_utm_source");
        // const firstUtmMedium = window.localStorage.getItem("first_utm_medium");
        // Tracker.gtag("event", "ads_sign_up", {
        //   event_category: "platform_ads_sign_up_success",
        //   utm_source: utmSource || "Unknown",
        //   utm_medium: utmMedium || "Unknown",
        //   first_utm_source: firstUtmSource || "Unknown",
        //   first_utm_medium: firstUtmMedium || "Unknown",
        //   ads_clerk_Name: user?.fullName,
        //   ads_clerk_user_id: user?.id,
        //   value: 1,
        // });
      } else if (!clerk.organization?.id) {
        setActive({ organization: data?.[0]?.organization?.id });
      }
    }
  }, [isLoading, clerk.loaded, user?.id]);

  return <div />;
};
